import { Link } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { PencilIcon } from 'lucide-react';

import { LinkToExternal } from '@/components/link-to-external.js';
import { SessionFeedbackStatus } from '@/components/session/session-feedback-status.js';
import { createTableRowSelectionCell } from '@/components/table/entity-table.js';
import { formatDateWithDayOfWeek, formatTimeRange, isNotNullish, isStudentRole } from '@airreading/common';
import {
  SessionFeedbackWithAssessment,
  SessionFeedbackIssueDetails,
  studentYearOption,
  StudentYearColorMap,
  toSessionFeedbackCompoundId,
} from '@airreading/common/data';
import { getDistrictColumn } from '@/lib/district-utils.js';
import { normalizeNewlines, stringIfNull } from '@/lib/string-utils.js';
import { getAttendanceDisplayValue } from '@/components/feedbacks/session-feedback-utils.js';
import { CopyableLink } from '@/components/copyable-link.js';
import { DEFAULT_PAGE_SIZE } from '@/lib/pagination.js';

const adminCol = createColumnHelper<SessionFeedbackWithAssessment>();

function feedbackTableRowTestId(
  tablePrefix: string | undefined,
  element: string,
  rowNumber: number,
): string | undefined {
  return tablePrefix ? `${tablePrefix}-table-${element}-${rowNumber}` : undefined;
}

function feedbackTableCellTestId(
  tablePrefix: string | undefined,
  field: string,
  rowNumber: number,
): string | undefined {
  return tablePrefix ? `${tablePrefix}-table-cell-${field}-${rowNumber}` : undefined;
}

export const FeedbacksTableColumnIds = {
  viewDetailsAdmin: 'viewDetailsAdmin',
  date: 'date',
  time: 'time',
  student: 'student',
  feedbackStatus: 'feedbackStatus',
  attendance: 'attendance',
  district: 'district',
  school: 'school',
  grade: 'grade',
  sessionTeacher: 'sessionTeacher',
  originalTeacher: 'originalTeacher',
  lessonName: 'lessonName',
  dailySummary: 'dailySummary',
  recordingLink: 'recordingLink',
  studentIssues: 'studentIssues',
  majorClassConcerns: 'majorClassConcerns',
  majorConcernsText: 'majorConcernsText',
  assessmentFinished: 'assessmentFinished',
  gradeLevelFluencyWPM: 'gradeLevelFluencyWPM',
  gradeLevelAccuracy: 'gradeLevelAccuracy',
  progressMonitorAccuracy: 'progressMonitorAccuracy',
  progressMonitorWPM: 'progressMonitorWPM',
  sessionIssues: 'sessionIssues',
  sessionFeedbackContent: 'sessionFeedbackContent',
  sessionMajorConcerns: 'sessionMajorConcerns',
} as const;

export type FeedbacksTableColumnId = (typeof FeedbacksTableColumnIds)[keyof typeof FeedbacksTableColumnIds];

export const feedbacksPageColumns = (
  displayTimeZone: string,
  schoolIdToDistrictName: Record<string, string>,
  enableBulkActions: boolean,
  isSelectAllPagesActive?: boolean,
  onSelectAllPagesChange?: (isActive: boolean) => void,
) =>
  [
    enableBulkActions
      ? createTableRowSelectionCell(adminCol, { isSelectAllPagesActive, onSelectAllPagesChange })
      : null,

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.viewDetailsAdmin,
      header: 'Edit',
      cell: ({ getValue }) => {
        return (
          <Link
            to="/sessions/session-feedbacks/$feedbackId"
            params={{ feedbackId: toSessionFeedbackCompoundId(getValue()) }}
            search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
          >
            <PencilIcon size={16} />
          </Link>
        );
      },
      size: 50,
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.date,
      header: 'Date (CT)',
      cell: ({ getValue }) => {
        const session = getValue();
        return <div>{formatDateWithDayOfWeek(session.startInstant, displayTimeZone)}</div>;
      },
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.time,
      header: 'Time (CT)',
      cell: ({ getValue }) => {
        const session = getValue();
        return <div>{formatTimeRange(session.startInstant, session.endInstant, displayTimeZone)}</div>;
      },
    }),

    adminCol.accessor('student', {
      id: FeedbacksTableColumnIds.student,
      header: 'Student',
      cell: ({ getValue }) => {
        const student = getValue();
        return <div>{student.fullNameOrEmail}</div>;
      },
    }),

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.feedbackStatus,
      header: 'Feedback status',
      cell: ({ getValue }) => {
        const feedback = getValue();
        const {
          session: { hoursCanceledBeforeStart, lesson },
        } = feedback;
        return (
          <SessionFeedbackStatus
            feedbacks={[feedback]}
            hoursCanceledBeforeStart={hoursCanceledBeforeStart}
            lessonShouldSkipFeedback={lesson.shouldSkipFeedback}
          />
        );
      },
    }),

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.attendance,
      header: 'Student attendance',
      cell: ({ getValue }) => {
        const feedback = getValue();
        return getAttendanceDisplayValue(feedback);
      },
    }),

    adminCol.accessor('student', {
      id: 'school',
      header: 'School',
      cell: ({ getValue }) => {
        const student = getValue();
        const studentRole = student.userRoles.find(isStudentRole);
        const school = studentRole?.school;
        if (!school) return <span>Unknown</span>;
        return (
          <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
            {school.name}
          </Link>
        );
      },
    }),

    adminCol.accessor('student', {
      id: 'district',
      header: 'District',
      cell: ({ getValue }) => {
        const student = getValue();
        const studentRole = student.userRoles.find(isStudentRole);
        const schoolId = studentRole?.school?.id;
        const districtName = schoolId ? schoolIdToDistrictName[schoolId] : null;
        const orgId = studentRole?.school?.orgId;
        if (!districtName || !orgId) return <span>Unknown</span>;
        return getDistrictColumn({
          orgId,
          orgName: districtName,
        });
      },
    }),

    adminCol.accessor('student', {
      id: FeedbacksTableColumnIds.grade,
      header: 'Grade',
      cell: ({ getValue }) => {
        const grade = getValue().studentProfile?.grade;
        if (!grade) return studentYearOption(stringIfNull(grade));
        const backgroundColor = StudentYearColorMap[grade];
        return (
          <div className="min-w-[80px] flex justify-start">
            <span className="text-xs rounded-full px-2 py-1 min-w-9 text-center" style={{ backgroundColor }}>
              {studentYearOption(grade)}
            </span>
          </div>
        );
      },
    }),

    adminCol.accessor('coach', {
      id: FeedbacksTableColumnIds.sessionTeacher,
      header: 'Session teacher',
      cell: ({ getValue }) => {
        const coach = getValue();
        return <div>{coach.fullNameOrEmail}</div>;
      },
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.lessonName,
      header: 'Lesson',
      cell: ({ getValue }) => {
        const { lessonLink, lesson } = getValue();
        if (!lesson) return '—';
        if (!lessonLink) return <div className="flex items-center gap-2">{lesson.name}</div>;

        return (
          <CopyableLink
            externalHref={lessonLink}
            className="text-blue-600 hover:text-blue-800 underline"
            showExternalHrefIcon={false}
          >
            {lesson.name}
          </CopyableLink>
        );
      },
    }),

    adminCol.accessor((row) => row.session.zoom, {
      id: FeedbacksTableColumnIds.recordingLink,
      header: 'Recording',
      cell: ({ getValue }) => {
        const sessionZoom = getValue();
        const recordingUrls = sessionZoom?.recordingUrls ?? [];
        if (recordingUrls.length === 0) {
          return <div>-</div>;
        }

        return (
          <div className="flex flex-col gap-1">
            {recordingUrls.map((url, index) => (
              <LinkToExternal
                key={`${url}-${index}`}
                href={url}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                {recordingUrls.length > 1 ? `Recording ${index + 1}` : 'View Recording'}
              </LinkToExternal>
            ))}
          </div>
        );
      },
    }),

    adminCol.accessor((row) => row.issues, {
      id: FeedbacksTableColumnIds.sessionIssues,
      header: 'Student issues',
      cell: ({ getValue }) => {
        const issues = getValue();
        if (!issues || issues.length === 0) {
          return <div>-</div>;
        }

        const readableIssues = issues.map((issue) => SessionFeedbackIssueDetails[issue]?.label || issue);

        return (
          <div className="text-sm max-w-[500px] break-words whitespace-normal overflow-wrap-anywhere">
            {readableIssues.join(', ')}
          </div>
        );
      },
    }),

    adminCol.accessor((row) => row.majorConcerns, {
      id: FeedbacksTableColumnIds.sessionMajorConcerns,
      header: 'Major student issue - Teacher report',
      cell: ({ getValue }) => {
        const majorConcerns = getValue();
        if (!majorConcerns) {
          return <div>-</div>;
        }

        return (
          <div className="max-w-[500px] break-words whitespace-normal overflow-wrap-anywhere" title={majorConcerns}>
            {majorConcerns}
          </div>
        );
      },
    }),

    adminCol.accessor((row) => row.feedbackContent, {
      id: FeedbacksTableColumnIds.sessionFeedbackContent,
      header: 'Class notes',
      cell: ({ getValue }) => {
        const feedbackContent = getValue();
        if (!feedbackContent) {
          return <div>-</div>;
        }

        return (
          <div className="max-w-[500px] break-words whitespace-normal overflow-wrap-anywhere" title={feedbackContent}>
            {feedbackContent}
          </div>
        );
      },
    }),

    adminCol.accessor((row) => row.progressMonitorWPM, {
      id: FeedbacksTableColumnIds.progressMonitorWPM,
      header: 'Reading Level Fluency (WPM)',
      cell: ({ getValue }) => {
        const wpm = getValue();
        return <div>{wpm !== null ? wpm.toFixed(2) : '-'}</div>;
      },
    }),

    adminCol.accessor((row) => row.progressMonitorAccuracy, {
      id: FeedbacksTableColumnIds.progressMonitorAccuracy,
      header: 'Reading Level Accuracy',
      cell: ({ getValue }) => {
        const accuracy = getValue();
        return <div>{accuracy !== null ? `${accuracy.toFixed(2)}%` : '-'}</div>;
      },
    }),

    adminCol.accessor((row) => row.gradeLevelWPM, {
      id: FeedbacksTableColumnIds.gradeLevelFluencyWPM,
      header: 'Grade Level Fluency (WPM)',
      cell: ({ getValue }) => {
        const wpm = getValue();
        return <div>{wpm !== null ? wpm.toFixed(2) : '-'}</div>;
      },
    }),

    adminCol.accessor((row) => row.gradeLevelAccuracy, {
      id: FeedbacksTableColumnIds.gradeLevelAccuracy,
      header: 'Grade Level Accuracy',
      cell: ({ getValue }) => {
        const accuracy = getValue();
        return <div>{accuracy !== null ? `${accuracy.toFixed(2)}%` : '-'}</div>;
      },
    }),
  ].filter(isNotNullish);

export const sessionFeedbacksPageColumns = (
  displayTimeZone: string,
  schoolIdToDistrictName: Record<string, string>,
  enableBulkActions: boolean,
  isSelectAllPagesActive?: boolean,
  onSelectAllPagesChange?: (isActive: boolean) => void,
  tablePrefix?: string,
) =>
  [
    enableBulkActions
      ? createTableRowSelectionCell(adminCol, { isSelectAllPagesActive, onSelectAllPagesChange })
      : null,

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.date,
      header: 'Date (CT)',
      cell: ({ getValue, row }) => {
        const session = getValue();
        const rowNumber = row.index + 1;
        return (
          <span data-testid={feedbackTableCellTestId(tablePrefix, 'date', rowNumber)}>
            {formatDateWithDayOfWeek(session.startInstant, displayTimeZone)}
          </span>
        );
      },
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.time,
      header: 'Time (CT)',
      cell: ({ getValue, row }) => {
        const session = getValue();
        const rowNumber = row.index + 1;
        return (
          <span data-testid={feedbackTableCellTestId(tablePrefix, 'time', rowNumber)}>
            {formatTimeRange(session.startInstant, session.endInstant, displayTimeZone)}
          </span>
        );
      },
    }),

    adminCol.accessor('student', {
      id: FeedbacksTableColumnIds.student,
      header: 'Student',
      cell: ({ getValue, row }) => {
        const student = getValue();
        const rowNumber = row.index + 1;
        return (
          <span data-testid={feedbackTableCellTestId(tablePrefix, 'student', rowNumber)}>
            {student.fullNameOrEmail}
          </span>
        );
      },
    }),

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.feedbackStatus,
      header: 'Feedback status',
      cell: ({ getValue, row }) => {
        const feedback = getValue();
        const rowNumber = row.index + 1;
        const {
          session: { hoursCanceledBeforeStart, lesson },
        } = feedback;
        return (
          <SessionFeedbackStatus
            feedbacks={[feedback]}
            hoursCanceledBeforeStart={hoursCanceledBeforeStart}
            lessonShouldSkipFeedback={lesson.shouldSkipFeedback}
            testId={feedbackTableCellTestId(tablePrefix, 'feedback-status', rowNumber)}
          />
        );
      },
    }),

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.attendance,
      header: 'Student attendance',
      cell: ({ getValue, row }) => {
        const feedback = getValue();
        const rowNumber = row.index + 1;
        return (
          <span data-testid={feedbackTableCellTestId(tablePrefix, 'attendance', rowNumber)}>
            {getAttendanceDisplayValue(feedback)}
          </span>
        );
      },
    }),

    adminCol.accessor('student', {
      id: 'school',
      header: 'School',
      cell: ({ getValue, row }) => {
        const student = getValue();
        const rowNumber = row.index + 1;
        const studentRole = student.userRoles.find(isStudentRole);
        const school = studentRole?.school;
        if (!school) {
          return <span data-testid={feedbackTableCellTestId(tablePrefix, 'school', rowNumber)}>Unknown</span>;
        }
        return (
          <Link
            to={'/orgs/schools/$schoolId/schedules'}
            params={{ schoolId: school.id }}
            className="text-blue-600"
            data-testid={feedbackTableRowTestId(tablePrefix, 'link-school', rowNumber)}
          >
            {school.name}
          </Link>
        );
      },
    }),

    adminCol.accessor('student', {
      id: 'district',
      header: 'District',
      cell: ({ getValue, row }) => {
        const student = getValue();
        const rowNumber = row.index + 1;
        const studentRole = student.userRoles.find(isStudentRole);
        const schoolId = studentRole?.school?.id;
        const districtName = schoolId ? schoolIdToDistrictName[schoolId] : null;
        if (!districtName) {
          return <span data-testid={feedbackTableCellTestId(tablePrefix, 'district', rowNumber)}>Unknown</span>;
        }
        return getDistrictColumn({
          orgName: districtName,
          shouldHaveLink: false,
          testId: feedbackTableCellTestId(tablePrefix, 'district', rowNumber),
        });
      },
    }),

    adminCol.accessor('student', {
      id: FeedbacksTableColumnIds.grade,
      header: 'Grade',
      cell: ({ getValue, row }) => {
        const grade = getValue().studentProfile?.grade;
        const rowNumber = row.index + 1;
        if (!grade) {
          return (
            <span data-testid={feedbackTableCellTestId(tablePrefix, 'grade', rowNumber)}>
              {studentYearOption(stringIfNull(grade))}
            </span>
          );
        }
        const backgroundColor = StudentYearColorMap[grade];
        return (
          <span
            className="text-xs rounded-full px-2 py-1 min-w-9 text-center inline-block"
            style={{ backgroundColor }}
            data-testid={feedbackTableCellTestId(tablePrefix, 'grade', rowNumber)}
          >
            {studentYearOption(grade)}
          </span>
        );
      },
    }),

    adminCol.accessor('coach', {
      id: FeedbacksTableColumnIds.sessionTeacher,
      header: 'Session teacher',
      cell: ({ getValue, row }) => {
        const coach = getValue();
        const rowNumber = row.index + 1;
        return (
          <span data-testid={feedbackTableCellTestId(tablePrefix, 'session-teacher', rowNumber)}>
            {coach.fullNameOrEmail}
          </span>
        );
      },
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.lessonName,
      header: 'Lesson',
      cell: ({ getValue, row }) => {
        const { lessonLink, lesson } = getValue();
        const rowNumber = row.index + 1;
        if (!lesson) {
          return <span data-testid={feedbackTableCellTestId(tablePrefix, 'lesson', rowNumber)}>—</span>;
        }
        if (!lessonLink) {
          return <span data-testid={feedbackTableCellTestId(tablePrefix, 'lesson', rowNumber)}>{lesson.name}</span>;
        }

        return (
          <CopyableLink
            externalHref={lessonLink}
            className="text-blue-600 hover:text-blue-800 underline"
            showExternalHrefIcon={false}
            data-testid={feedbackTableRowTestId(tablePrefix, 'link-lesson', rowNumber)}
            copyButtonTestId={feedbackTableRowTestId(tablePrefix, 'button-copy-lesson', rowNumber)}
          >
            {lesson.name}
          </CopyableLink>
        );
      },
    }),

    adminCol.accessor((row) => row.session.zoom, {
      id: FeedbacksTableColumnIds.recordingLink,
      header: 'Recording',
      cell: ({ getValue, row }) => {
        const sessionZoom = getValue();
        const rowNumber = row.index + 1;
        const recordingUrls = sessionZoom?.recordingUrls ?? [];
        if (recordingUrls.length === 0) {
          return <span data-testid={feedbackTableCellTestId(tablePrefix, 'recording', rowNumber)}>-</span>;
        }

        return (
          <>
            {recordingUrls.map((url, index) => (
              <LinkToExternal
                key={`${url}-${index}`}
                href={url}
                className="text-blue-600 hover:text-blue-800 underline block"
                data-testid={feedbackTableCellTestId(
                  tablePrefix,
                  recordingUrls.length === 1 ? 'recording' : `recording-${index + 1}`,
                  rowNumber,
                )}
              >
                {recordingUrls.length > 1 ? `Recording ${index + 1}` : 'View Recording'}
              </LinkToExternal>
            ))}
          </>
        );
      },
    }),

    adminCol.accessor((row) => row.progressMonitorWPM, {
      id: FeedbacksTableColumnIds.progressMonitorWPM,
      header: 'Reading Level Fluency (WPM)',
      cell: ({ getValue, row }) => {
        const wpm = getValue();
        const rowNumber = row.index + 1;
        return (
          <span data-testid={feedbackTableCellTestId(tablePrefix, 'reading-level-fluency-wpm', rowNumber)}>
            {wpm !== null ? wpm.toFixed(2) : '-'}
          </span>
        );
      },
    }),

    adminCol.accessor((row) => row.progressMonitorAccuracy, {
      id: FeedbacksTableColumnIds.progressMonitorAccuracy,
      header: 'Reading Level Accuracy',
      cell: ({ getValue, row }) => {
        const accuracy = getValue();
        const rowNumber = row.index + 1;
        return (
          <span data-testid={feedbackTableCellTestId(tablePrefix, 'reading-level-accuracy', rowNumber)}>
            {accuracy !== null ? `${accuracy.toFixed(2)}%` : '-'}
          </span>
        );
      },
    }),

    adminCol.accessor((row) => row.gradeLevelWPM, {
      id: FeedbacksTableColumnIds.gradeLevelFluencyWPM,
      header: 'Grade Level Fluency (WPM)',
      cell: ({ getValue, row }) => {
        const wpm = getValue();
        const rowNumber = row.index + 1;
        return (
          <span data-testid={feedbackTableCellTestId(tablePrefix, 'grade-level-fluency-wpm', rowNumber)}>
            {wpm !== null ? wpm.toFixed(2) : '-'}
          </span>
        );
      },
    }),

    adminCol.accessor((row) => row.gradeLevelAccuracy, {
      id: FeedbacksTableColumnIds.gradeLevelAccuracy,
      header: 'Grade Level Accuracy',
      cell: ({ getValue, row }) => {
        const accuracy = getValue();
        const rowNumber = row.index + 1;
        return (
          <span data-testid={feedbackTableCellTestId(tablePrefix, 'grade-level-accuracy', rowNumber)}>
            {accuracy !== null ? `${accuracy.toFixed(2)}%` : '-'}
          </span>
        );
      },
    }),
  ].filter(isNotNullish);

export const teacherFeedbacksPageColumns = (
  displayTimeZone: string,
  schoolIdToDistrictName: Record<string, string>,
  enableBulkActions: boolean,
  isSelectAllPagesActive?: boolean,
  onSelectAllPagesChange?: (isActive: boolean) => void,
) =>
  [
    enableBulkActions
      ? createTableRowSelectionCell(adminCol, { isSelectAllPagesActive, onSelectAllPagesChange })
      : null,

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.date,
      header: 'Date',
      cell: ({ getValue }) => {
        const session = getValue();
        return <div>{formatDateWithDayOfWeek(session.startInstant, displayTimeZone)}</div>;
      },
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.time,
      header: 'Time (CT)',
      cell: ({ getValue }) => {
        const session = getValue();
        return <div>{formatTimeRange(session.startInstant, session.endInstant, displayTimeZone)}</div>;
      },
    }),

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.feedbackStatus,
      header: 'Feedback status',
      cell: ({ getValue }) => {
        const feedback = getValue();
        const {
          session: { hoursCanceledBeforeStart, lesson },
        } = feedback;
        return (
          <SessionFeedbackStatus
            feedbacks={[feedback]}
            hoursCanceledBeforeStart={hoursCanceledBeforeStart}
            lessonShouldSkipFeedback={lesson.shouldSkipFeedback}
          />
        );
      },
    }),

    adminCol.accessor('student', {
      id: FeedbacksTableColumnIds.student,
      header: 'Student',
      cell: ({ getValue }) => {
        const student = getValue();
        return <div>{student.fullNameOrEmail}</div>;
      },
    }),

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.attendance,
      header: 'Student attendance',
      cell: ({ getValue }) => {
        const feedback = getValue();
        return getAttendanceDisplayValue(feedback);
      },
    }),

    adminCol.accessor('student', {
      id: 'school',
      header: 'School - District',
      cell: ({ getValue }) => {
        const student = getValue();
        const studentRole = student.userRoles.find(isStudentRole);
        const school = studentRole?.school;
        if (!school) return <span>Unknown - Unknown</span>;
        const schoolId = school.id;
        const districtName = schoolIdToDistrictName[schoolId];
        return (
          <div>
            <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
              {school.name} - <span className="text-blue-600">{districtName}</span>
            </Link>
          </div>
        );
      },
    }),

    adminCol.accessor('student', {
      id: FeedbacksTableColumnIds.grade,
      header: 'Grade',
      cell: ({ getValue }) => {
        const grade = getValue().studentProfile?.grade;
        if (!grade) return studentYearOption(stringIfNull(grade));
        const backgroundColor = StudentYearColorMap[grade];
        return (
          <div className="min-w-[80px] flex justify-start">
            <span className="text-xs rounded-full px-2 py-1 min-w-9 text-center" style={{ backgroundColor }}>
              {studentYearOption(grade)}
            </span>
          </div>
        );
      },
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.lessonName,
      header: 'Lesson',
      cell: ({ getValue }) => {
        const { lessonLink, lesson } = getValue();
        if (!lesson) return '—';
        if (!lessonLink) return <div className="flex items-center gap-2">{lesson.name}</div>;

        return (
          <CopyableLink
            externalHref={lessonLink}
            className="text-blue-600 hover:text-blue-800 underline"
            showExternalHrefIcon={false}
          >
            {lesson.name}
          </CopyableLink>
        );
      },
    }),

    adminCol.accessor((row) => row.progressMonitorWPM, {
      id: FeedbacksTableColumnIds.progressMonitorWPM,
      header: 'Reading Level Fluency (WPM)',
      cell: ({ getValue }) => {
        const wpm = getValue();
        return <div>{wpm !== null ? wpm.toFixed(2) : '-'}</div>;
      },
    }),

    adminCol.accessor((row) => row.progressMonitorAccuracy, {
      id: FeedbacksTableColumnIds.progressMonitorAccuracy,
      header: 'Reading Level Accuracy',
      cell: ({ getValue }) => {
        const accuracy = getValue();
        return <div>{accuracy !== null ? `${accuracy.toFixed(2)}%` : '-'}</div>;
      },
    }),

    adminCol.accessor((row) => row.gradeLevelWPM, {
      id: FeedbacksTableColumnIds.gradeLevelFluencyWPM,
      header: 'Grade Level Fluency (WPM)',
      cell: ({ getValue }) => {
        const wpm = getValue();
        return <div>{wpm !== null ? wpm.toFixed(2) : '-'}</div>;
      },
    }),

    adminCol.accessor((row) => row.gradeLevelAccuracy, {
      id: FeedbacksTableColumnIds.gradeLevelAccuracy,
      header: 'Grade Level Accuracy',
      cell: ({ getValue }) => {
        const accuracy = getValue();
        return <div>{accuracy !== null ? `${accuracy.toFixed(2)}%` : '-'}</div>;
      },
    }),
  ].filter(isNotNullish);

export const studentFeedbacksPageColumns = (
  displayTimeZone: string,
  schoolIdToDistrictName: Record<string, string>,
  enableBulkActions: boolean,
  isSelectAllPagesActive?: boolean,
  onSelectAllPagesChange?: (isActive: boolean) => void,
  tablePrefix?: string,
) =>
  [
    enableBulkActions
      ? createTableRowSelectionCell(adminCol, {
          isSelectAllPagesActive,
          onSelectAllPagesChange,
          selectAllTestId: tablePrefix ? `${tablePrefix}-table-checkbox-select-all` : undefined,
          rowSelectTestId: tablePrefix ? (rowNumber) => `${tablePrefix}-table-checkbox-select-${rowNumber}` : undefined,
        })
      : null,

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.viewDetailsAdmin,
      header: 'Edit',
      cell: ({ getValue, row }) => {
        const rowNumber = row.index + 1;
        return (
          <Link
            to="/sessions/session-feedbacks/$feedbackId"
            params={{ feedbackId: toSessionFeedbackCompoundId(getValue()) }}
            search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
          >
            <PencilIcon
              size={16}
              data-testid={feedbackTableRowTestId(tablePrefix, 'button-edit-feedback', rowNumber)}
            />
          </Link>
        );
      },
      size: 50,
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.date,
      header: 'Date (CT)',
      cell: ({ getValue, row }) => {
        const session = getValue();
        const rowNumber = row.index + 1;
        return (
          <span data-testid={feedbackTableCellTestId(tablePrefix, 'date', rowNumber)}>
            {formatDateWithDayOfWeek(session.startInstant, displayTimeZone)}
          </span>
        );
      },
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.time,
      header: 'Time (CT)',
      cell: ({ getValue, row }) => {
        const session = getValue();
        const rowNumber = row.index + 1;
        return (
          <span data-testid={feedbackTableCellTestId(tablePrefix, 'time', rowNumber)}>
            {formatTimeRange(session.startInstant, session.endInstant, displayTimeZone)}
          </span>
        );
      },
    }),

    adminCol.accessor('student', {
      id: FeedbacksTableColumnIds.student,
      header: 'Student',
      cell: ({ getValue, row }) => {
        const student = getValue();
        const rowNumber = row.index + 1;
        return (
          <span data-testid={feedbackTableCellTestId(tablePrefix, 'student', rowNumber)}>
            {student.fullNameOrEmail}
          </span>
        );
      },
    }),

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.feedbackStatus,
      header: 'Feedback status',
      cell: ({ getValue, row }) => {
        const feedback = getValue();
        const rowNumber = row.index + 1;
        const {
          session: { hoursCanceledBeforeStart, lesson },
        } = feedback;
        return (
          <SessionFeedbackStatus
            feedbacks={[feedback]}
            hoursCanceledBeforeStart={hoursCanceledBeforeStart}
            lessonShouldSkipFeedback={lesson.shouldSkipFeedback}
            testId={feedbackTableCellTestId(tablePrefix, 'feedback-status', rowNumber)}
          />
        );
      },
    }),

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.attendance,
      header: 'Student attendance',
      cell: ({ getValue, row }) => {
        const feedback = getValue();
        const rowNumber = row.index + 1;
        return (
          <span data-testid={feedbackTableCellTestId(tablePrefix, 'attendance', rowNumber)}>
            {getAttendanceDisplayValue(feedback)}
          </span>
        );
      },
    }),

    adminCol.accessor('student', {
      id: 'school',
      header: 'School',
      cell: ({ getValue, row }) => {
        const student = getValue();
        const rowNumber = row.index + 1;
        const studentRole = student.userRoles.find(isStudentRole);
        const school = studentRole?.school;
        if (!school) {
          return <span data-testid={feedbackTableCellTestId(tablePrefix, 'school', rowNumber)}>Unknown</span>;
        }
        return (
          <Link
            to={'/orgs/schools/$schoolId/schedules'}
            params={{ schoolId: school.id }}
            className="text-blue-600"
            data-testid={feedbackTableRowTestId(tablePrefix, 'link-school', rowNumber)}
          >
            {school.name}
          </Link>
        );
      },
    }),

    adminCol.accessor('student', {
      id: 'district',
      header: 'District',
      cell: ({ getValue, row }) => {
        const student = getValue();
        const rowNumber = row.index + 1;
        const studentRole = student.userRoles.find(isStudentRole);
        const schoolId = studentRole?.school?.id;
        const districtName = schoolId ? schoolIdToDistrictName[schoolId] : null;
        const orgId = studentRole?.school?.orgId;
        if (!districtName || !orgId) {
          return <span data-testid={feedbackTableCellTestId(tablePrefix, 'district', rowNumber)}>Unknown</span>;
        }
        return getDistrictColumn({
          orgId,
          orgName: districtName,
          testId: feedbackTableCellTestId(tablePrefix, 'district', rowNumber),
        });
      },
    }),

    adminCol.accessor('student', {
      id: FeedbacksTableColumnIds.grade,
      header: 'Grade',
      cell: ({ getValue, row }) => {
        const grade = getValue().studentProfile?.grade;
        const rowNumber = row.index + 1;
        if (!grade) {
          return (
            <span data-testid={feedbackTableCellTestId(tablePrefix, 'grade', rowNumber)}>
              {studentYearOption(stringIfNull(grade))}
            </span>
          );
        }
        const backgroundColor = StudentYearColorMap[grade];
        return (
          <span
            className="text-xs rounded-full px-2 py-1 min-w-9 text-center inline-block"
            style={{ backgroundColor }}
            data-testid={feedbackTableCellTestId(tablePrefix, 'grade', rowNumber)}
          >
            {studentYearOption(grade)}
          </span>
        );
      },
    }),

    adminCol.accessor('coach', {
      id: FeedbacksTableColumnIds.sessionTeacher,
      header: 'Session teacher',
      cell: ({ getValue, row }) => {
        const coach = getValue();
        const rowNumber = row.index + 1;
        return (
          <span data-testid={feedbackTableCellTestId(tablePrefix, 'session-teacher', rowNumber)}>
            {coach.fullNameOrEmail}
          </span>
        );
      },
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.lessonName,
      header: 'Lesson',
      cell: ({ getValue, row }) => {
        const { lessonLink, lesson } = getValue();
        const rowNumber = row.index + 1;
        if (!lesson) {
          return <span data-testid={feedbackTableCellTestId(tablePrefix, 'lesson', rowNumber)}>—</span>;
        }
        if (!lessonLink) {
          return <span data-testid={feedbackTableCellTestId(tablePrefix, 'lesson', rowNumber)}>{lesson.name}</span>;
        }

        return (
          <CopyableLink
            externalHref={lessonLink}
            className="text-blue-600 hover:text-blue-800 underline"
            showExternalHrefIcon={false}
            data-testid={feedbackTableRowTestId(tablePrefix, 'link-lesson', rowNumber)}
            copyButtonTestId={feedbackTableRowTestId(tablePrefix, 'button-copy-lesson', rowNumber)}
          >
            {lesson.name}
          </CopyableLink>
        );
      },
    }),

    adminCol.accessor((row) => row.dailySummary, {
      id: FeedbacksTableColumnIds.dailySummary,
      header: 'Daily Summary',
      cell: ({ getValue, row }) => {
        const dailySummary = getValue();
        const rowNumber = row.index + 1;
        return (
          <span
            className="whitespace-pre-line"
            data-testid={feedbackTableCellTestId(tablePrefix, 'daily-summary', rowNumber)}
          >
            {dailySummary ? normalizeNewlines(dailySummary) : '-'}
          </span>
        );
      },
    }),

    adminCol.accessor((row) => row.session.zoom, {
      id: FeedbacksTableColumnIds.recordingLink,
      header: 'Recording',
      cell: ({ getValue, row }) => {
        const sessionZoom = getValue();
        const rowNumber = row.index + 1;
        const recordingUrls = sessionZoom?.recordingUrls ?? [];
        if (recordingUrls.length === 0) {
          return <span data-testid={feedbackTableCellTestId(tablePrefix, 'recording', rowNumber)}>-</span>;
        }

        return (
          <>
            {recordingUrls.map((url, index) => (
              <LinkToExternal
                key={`${url}-${index}`}
                href={url}
                className="text-blue-600 hover:text-blue-800 underline block"
                data-testid={feedbackTableCellTestId(
                  tablePrefix,
                  recordingUrls.length === 1 ? 'recording' : `recording-${index + 1}`,
                  rowNumber,
                )}
              >
                {recordingUrls.length > 1 ? `Recording ${index + 1}` : 'View Recording'}
              </LinkToExternal>
            ))}
          </>
        );
      },
    }),

    adminCol.accessor((row) => row.issues, {
      id: FeedbacksTableColumnIds.sessionIssues,
      header: 'Student issues',
      cell: ({ getValue, row }) => {
        const issues = getValue();
        const rowNumber = row.index + 1;
        if (!issues || issues.length === 0) {
          return <span data-testid={feedbackTableCellTestId(tablePrefix, 'student-issues', rowNumber)}>-</span>;
        }

        const readableIssues = issues.map((issue) => SessionFeedbackIssueDetails[issue]?.label || issue);

        return (
          <span
            className="text-sm max-w-[500px] break-words whitespace-normal overflow-wrap-anywhere"
            data-testid={feedbackTableCellTestId(tablePrefix, 'student-issues', rowNumber)}
          >
            {readableIssues.join(', ')}
          </span>
        );
      },
    }),

    adminCol.accessor((row) => row.majorConcerns, {
      id: FeedbacksTableColumnIds.sessionMajorConcerns,
      header: 'Major student issue - Teacher report',
      cell: ({ getValue, row }) => {
        const majorConcerns = getValue();
        const rowNumber = row.index + 1;
        if (!majorConcerns) {
          return <span data-testid={feedbackTableCellTestId(tablePrefix, 'major-concerns', rowNumber)}>-</span>;
        }

        return (
          <span
            className="max-w-[500px] break-words whitespace-normal overflow-wrap-anywhere"
            title={majorConcerns}
            data-testid={feedbackTableCellTestId(tablePrefix, 'major-concerns', rowNumber)}
          >
            {majorConcerns}
          </span>
        );
      },
    }),

    adminCol.accessor((row) => row.feedbackContent, {
      id: FeedbacksTableColumnIds.sessionFeedbackContent,
      header: 'Class notes',
      cell: ({ getValue, row }) => {
        const feedbackContent = getValue();
        const rowNumber = row.index + 1;
        if (!feedbackContent) {
          return <span data-testid={feedbackTableCellTestId(tablePrefix, 'class-notes', rowNumber)}>-</span>;
        }

        return (
          <span
            className="max-w-[500px] break-words whitespace-normal overflow-wrap-anywhere"
            title={feedbackContent}
            data-testid={feedbackTableCellTestId(tablePrefix, 'class-notes', rowNumber)}
          >
            {feedbackContent}
          </span>
        );
      },
    }),

    adminCol.accessor((row) => row.progressMonitorWPM, {
      id: FeedbacksTableColumnIds.progressMonitorWPM,
      header: 'Reading Level Fluency (WPM)',
      cell: ({ getValue, row }) => {
        const wpm = getValue();
        const rowNumber = row.index + 1;
        return (
          <span data-testid={feedbackTableCellTestId(tablePrefix, 'reading-level-fluency-wpm', rowNumber)}>
            {wpm !== null ? wpm.toFixed(2) : '-'}
          </span>
        );
      },
    }),

    adminCol.accessor((row) => row.progressMonitorAccuracy, {
      id: FeedbacksTableColumnIds.progressMonitorAccuracy,
      header: 'Reading Level Accuracy',
      cell: ({ getValue, row }) => {
        const accuracy = getValue();
        const rowNumber = row.index + 1;
        return (
          <span data-testid={feedbackTableCellTestId(tablePrefix, 'reading-level-accuracy', rowNumber)}>
            {accuracy !== null ? `${accuracy.toFixed(2)}%` : '-'}
          </span>
        );
      },
    }),

    adminCol.accessor((row) => row.gradeLevelWPM, {
      id: FeedbacksTableColumnIds.gradeLevelFluencyWPM,
      header: 'Grade Level Fluency (WPM)',
      cell: ({ getValue, row }) => {
        const wpm = getValue();
        const rowNumber = row.index + 1;
        return (
          <span data-testid={feedbackTableCellTestId(tablePrefix, 'grade-level-fluency-wpm', rowNumber)}>
            {wpm !== null ? wpm.toFixed(2) : '-'}
          </span>
        );
      },
    }),

    adminCol.accessor((row) => row.gradeLevelAccuracy, {
      id: FeedbacksTableColumnIds.gradeLevelAccuracy,
      header: 'Grade Level Accuracy',
      cell: ({ getValue, row }) => {
        const accuracy = getValue();
        const rowNumber = row.index + 1;
        return (
          <span data-testid={feedbackTableCellTestId(tablePrefix, 'grade-level-accuracy', rowNumber)}>
            {accuracy !== null ? `${accuracy.toFixed(2)}%` : '-'}
          </span>
        );
      },
    }),
  ].filter(isNotNullish);
